import React, { useState } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Container, Typography, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// Import step components from the steps barrel file
import {
  CompanyInfoStep,
  BusinessDetailsStep,
  PreferencesStep,
  ConfirmationStep,
  SuccessStep
} from './steps';

const steps = ['Company Info', 'Business Details', 'Preferences', 'Confirmation'];

import { FormData, Preferences } from './types';

const NewOnboardingFlow = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<Partial<FormData>>({
    preferences: {
      emailUpdates: true,
      productRecommendations: true,
      newsletter: false,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateStep = (step: number): boolean => {
    // Clear previous errors
    setError(null);
    
    // Validate based on the current step
    switch (step) {
      case 0: // Company Info
        if (!formData.companyName?.trim()) {
          setError('Company name is required');
          return false;
        }
        if (!formData.industry) {
          setError('Industry is required');
          return false;
        }
        if (!formData.country) {
          setError('Country is required');
          return false;
        }
        break;
      case 1: // Business Details
        if (!formData.companySize) {
          setError('Company size is required');
          return false;
        }
        if (!formData.annualRevenue) {
          setError('Annual revenue is required');
          return false;
        }
        break;
      // Steps 2 (Preferences) and 3 (Confirmation) don't need validation
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    // Validate all required fields before submission
    const requiredFields = ['companyName', 'industry', 'country', 'companySize', 'annualRevenue'] as const;
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare the submission data with default preferences
      const submissionData: FormData = {
        ...formData as Required<Pick<FormData, 'companyName' | 'industry' | 'country' | 'companySize' | 'annualRevenue'>>,
        website: formData.website || '',
        preferences: {
          emailUpdates: formData.preferences?.emailUpdates ?? true,
          productRecommendations: formData.preferences?.productRecommendations ?? true,
          newsletter: formData.preferences?.newsletter ?? false,
        },
      };
      
      console.log('Submitting form data:', submissionData);
      
      // Simulate API call with 10% chance of failure
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          if (Math.random() < 0.1) {
            reject(new Error('Network error'));
          } else {
            resolve(submissionData);
          }
        }, 1000);
      });
      
      // Only move to success step if submission was successful
      handleNext();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(`Failed to submit the form: ${errorMessage}. Please try again.`);
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (newData: Partial<FormData>) => {
    setFormData(prev => {
      // Create default preferences object
      const defaultPreferences: Preferences = {
        emailUpdates: true,
        productRecommendations: true,
        newsletter: false,
      };

      // If newData contains preferences, merge them with existing preferences
      if (newData.preferences) {
        return {
          ...prev,
          ...newData,
          preferences: {
            ...defaultPreferences,
            ...prev.preferences,
            ...newData.preferences,
          },
        };
      }
      
      // For non-preference updates, just merge with existing data
      return {
        ...prev,
        ...newData,
      };
    });
    
    // Clear any existing error when form data is updated
    if (error) {
      setError(null);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <CompanyInfoStep
            data={formData}
            updateData={updateFormData}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <BusinessDetailsStep
            data={formData}
            updateData={updateFormData}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <PreferencesStep
            data={formData}
            updateData={updateFormData}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <ConfirmationStep
            data={formData}
            onBack={handleBack}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      case 4:
        return <SuccessStep companyName={formData.companyName} />;
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Welcome to ASCIN
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
          Let's get you set up in just a few steps
        </Typography>
        
        <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ mt: 4, minHeight: '300px' }}>
          {error && (
            <Paper 
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: 'error.light',
                color: 'error.contrastText',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ErrorOutlineIcon fontSize="small" />
              </Box>
              <Typography variant="body2">
                {error}
              </Typography>
            </Paper>
          )}
          {renderStepContent(activeStep)}
        </Box>
      </Paper>
    </Container>
  );
};

export default NewOnboardingFlow;
